'use client';

import { fetchMe } from '@eliadmin/core/src/api/customers/customers-api';
import {
  CUSTOMER_CREDIT_TYPES,
  getCustomerCreditBalance,
} from '@eliadmin/core/src/api/customers/customer-credit-utils';
import { unlockCurriculum } from '@eliadmin/core/src/api/jobportal/orders-api';
import { Button } from '@eliadmin/core/src/components/ui/button';
import {
  ResponsiveDialog,
  ResponsiveDialogContent,
  ResponsiveDialogFooter,
  ResponsiveDialogHeader,
} from '@eliadmin/core/src/components/ui/responsive-dialog';
import { Spinner } from '@eliadmin/core/src/components/ui/spinner';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { initCustomerStore, useCustomerStore } from '@eliadmin/core/src/stores/customer-store';
import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';
import { getSlugFromPage } from '@eliadmin/core/src/utils/url-utils';
import { useQueryClient } from '@tanstack/react-query';
import Link from '@eliadmin/core/src/components/link';
import { useRouter } from 'next/navigation';
import { useState } from 'react';

type CurriculumBuyDialogProps = {
  slug: string;
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
};

export const CurriculumBuyDialog = ({ slug, isOpen, setIsOpen }: CurriculumBuyDialogProps) => {
  const { t } = useTranslations();
  const { me } = useCustomerStore();
  const locale = useLocaleStore(s => s.currentLocale);
  const [isUnlocking, setIsUnlocking] = useState(false);
  const [wasUnlocked, setWasUnlocked] = useState(false);
  const queryClient = useQueryClient();
  const router = useRouter();
  const curriculumBalance = getCustomerCreditBalance(
    me?.customer,
    CUSTOMER_CREDIT_TYPES.CURRICULUM_VITAES_BALANCE,
  );

  const handleUnlockCurriculum = () => {
    setIsUnlocking(true);
    unlockCurriculum({
      slug,
      authToken: me?.authToken || '',
    })
      .then(async () => {
        setIsUnlocking(false);
        setWasUnlocked(true);
        queryClient.invalidateQueries({ queryKey: ['TODO:CacheKey.CURRICULUMS'] });

        if (me?.authToken) {
          try {
            const refreshedMe = await fetchMe({ locale, authToken: me.authToken });
            initCustomerStore({ authToken: me.authToken, customer: refreshedMe.data });
          } catch {
            // Unlocking succeeded; the balance will refresh on the next customer fetch.
          }
        }
      })
      .catch(() => {
        router.push(getSlugFromPage('price-list'));
      });
  };

  return (
    <ResponsiveDialog open={isOpen} onOpenChange={setIsOpen}>
      <ResponsiveDialogContent className="z-[9999] px-4">
        {!wasUnlocked && !isUnlocking && (
          <>
            <ResponsiveDialogHeader>
              <div className="mb-4 max-w-full truncate break-words border-b border-gray-200 pb-2 text-center text-2xl font-extrabold tracking-tight text-primary">
                {t('text2105')}
              </div>
            </ResponsiveDialogHeader>
            <div className="text-sm font-semibold">
              {t('text3517')}: <span className="text-secondary">{curriculumBalance}</span>
            </div>
            <ResponsiveDialogFooter>
              <div className="flex w-full justify-between">
                <Button variant="default-alt" onClick={() => setIsOpen(false)}>
                  {t('text377') /* Cancel */}
                </Button>
                <Button onClick={() => handleUnlockCurriculum()}>{t('text2107')}</Button>
              </div>
            </ResponsiveDialogFooter>
          </>
        )}
        {isUnlocking && (
          <div className="flex flex-col items-center justify-center py-6">
            <Spinner className="h-8 w-8 text-secondary md:h-10 md:w-10" />
          </div>
        )}
        {wasUnlocked && !isUnlocking && (
          <>
            <div className="flex flex-col items-center justify-center gap-4">
              <h2 className="text-lg font-bold md:text-xl">{t('text2110')}</h2>
              <Button asChild>
                <a href={slug}>{t('text2111')}</a>
              </Button>
            </div>
          </>
        )}
      </ResponsiveDialogContent>
    </ResponsiveDialog>
  );
};
